import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ReadingTimeDisplay } from "../components/ReadingTimeDisplay";
import { getGroupMetadata } from "../utils/readingTimeUtils";

export const GroupSelector = () => {
  const selectedGroup = useAppState((state) => state.selectedGroup);
  const currentDocument = useAppState((state) => state.currentDocument);
  const isNotebookOpen = useAppState((state) => state.isNoteEditorOpen);
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(0);
  const manualNavRef = useRef(false);

  const groups = currentDocument?.groups ?? [];
  const shouldHide =
    isNotebookOpen ||
    !currentDocument ||
    groups.length <= 1;

  const selectedIndex = selectedGroup
    ? groups.findIndex((group) => group.id === selectedGroup.id)
    : -1;

  // Pagination logic - sliding window that moves by 1 group at a time
  const CARDS_PER_PAGE = 4;
  const totalGroups = groups.length;
  
  // Calculate start index - each page moves by 1 group
  const maxStartIndex = Math.max(0, totalGroups - CARDS_PER_PAGE);
  const startIndex = Math.min(currentPage, maxStartIndex);
  const endIndex = startIndex + CARDS_PER_PAGE;
  const visibleGroups = groups.slice(startIndex, endIndex);
  
  // Total pages = number of possible starting positions
  const totalPages = totalGroups <= CARDS_PER_PAGE ? 1 : maxStartIndex + 1;

  // Adjust visible window ONLY when selection changes and the selected group is not visible
  useEffect(() => {
    // Skip auto-adjust once right after a manual navigation
    if (manualNavRef.current) {
      manualNavRef.current = false;
      return;
    }
    if (selectedIndex >= 0 && currentDocument?.groups.length && currentDocument.groups.length > 1) {
      const maxStartIndexLocal = Math.max(0, totalGroups - CARDS_PER_PAGE);
      const currentStartIndex = Math.min(currentPage, maxStartIndexLocal);
      const currentEndIndex = currentStartIndex + CARDS_PER_PAGE;

      // If selected group is left of window, snap left to its index
      if (selectedIndex < currentStartIndex) {
        const newStart = Math.max(0, Math.min(selectedIndex, maxStartIndexLocal));
        console.log('Adjusting left to include selected group at index', selectedIndex, '→', newStart);
        setCurrentPage(newStart);
      }
      // If selected group is right of window, shift so it appears at the right edge
      else if (selectedIndex >= currentEndIndex) {
        const newStart = Math.max(0, Math.min(selectedIndex - CARDS_PER_PAGE + 1, maxStartIndexLocal));
        console.log('Adjusting right to include selected group at index', selectedIndex, '→', newStart);
        setCurrentPage(newStart);
      }
    }
  }, [selectedIndex, totalGroups, currentDocument?.groups.length, currentPage, CARDS_PER_PAGE]);

  // Clamp window start when the number of groups changes
  useEffect(() => {
    const maxStartIndexLocal = Math.max(0, totalGroups - CARDS_PER_PAGE);
    if (currentPage > maxStartIndexLocal) {
      setCurrentPage(maxStartIndexLocal);
    }
  }, [totalGroups, currentPage, CARDS_PER_PAGE]);
  
  if (shouldHide) {
    return null;
  }

  return (
    <div className="group/selector">
      {/* Hover trigger area - only the bottom 50px is interactive */}
      <div className="fixed bottom-0 left-0 right-0 h-[50px] z-20" />

      {/* Main content */}
      <div className="fixed bottom-0 left-0 right-0 w-full z-10 pointer-events-none">
        <div className="bg-white p-4 rounded-t-lg shadow-lg w-full h-[200px] transform translate-y-full transition-transform duration-300 ease-in-out group-hover/selector:translate-y-0">
          <div className="flex items-end h-full gap-4 pointer-events-auto relative">
            {/* Previous arrow */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 bottom-12 z-10"
              disabled={currentPage <= 0}
              onClick={() => {
                console.log('Previous button clicked! Current page:', currentPage);
                manualNavRef.current = true;
                setCurrentPage(Math.max(0, currentPage - 1));
              }}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex-1 mx-16">
              <div className="flex gap-4 pb-2 justify-center items-end">
                {visibleGroups.map((group) => {
                  const isSelected = group.id === selectedGroup?.id;
                  const metadata = currentDocument ? getGroupMetadata(group, currentDocument) : null;
                  const previewBlock = currentDocument?.blocks
                    .filter((bl) => bl.type === "text")
                    .find((b) => group.blockIds.includes(b.id));

                  return (
                    <Card
                      key={group.id}
                      className={`w-[240px] h-[130px] cursor-pointer transition-all border py-3 ${
                        isSelected
                          ? "border-brand shadow-sm h-[162px]"
                          : "hover:border-gray-300"
                      }`}
                      onClick={() =>
                        dispatch({ type: "SET_SELECTED_GROUP", payload: group })
                      }
                    >
                      <CardContent className="px-4 py-0 flex flex-col justify-between h-full">
                        <span className="font-semibold text-sm mb-2 line-clamp-1">
                          {group.title}
                        </span>
                        <p className="text-xs text-muted-foreground line-clamp-3 mt-1">
                          {previewBlock?.value || "No content"}
                        </p>
                        {isSelected && metadata && (
                          <ReadingTimeDisplay
                            totalWordCount={metadata.wordCount}
                            estimatedTime={metadata.estimatedTime}
                            layout="horizontal"
                            className="mt-4"
                          />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Next arrow */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 bottom-12 z-10"
              disabled={currentPage >= totalPages - 1}
              onClick={() => {
                console.log('Next button clicked! Current page:', currentPage, 'Total pages:', totalPages);
                manualNavRef.current = true;
                setCurrentPage(Math.min(totalPages - 1, currentPage + 1));
              }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
